import React, { useEffect, useMemo, useState } from "react";
import {
  format,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
} from "date-fns";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { formatDate } from "@/lib/dates";
import {
  RefreshCw,
  CalendarDays,
  BarChart3,
  TrendingUp,
} from "lucide-react";
import {
  ResponsiveContainer,
  ComposedChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Bar,
  Line,
} from "recharts";
import RangeDatePicker from "@/components/RangeDatePicker";



type RangeMode = "day" | "week" | "month";
type LongJobMode = "all" | "exclude" | "only";

// One row per day returned by rpc_taxicaller_range_actual_by_day
type RangeDayRow = {
  active_date: string;
  iso_dow: number;
  dow_label: string;
  distinct_jobs: number;
  distinct_vehicles: number;
  total_job_hours: number;
  peak_vehicles: number;
  peak_drivers: number;
  peak_hour: number;
  peak_hour_jobs: number;
};

// One row per hour from rpc_taxicaller_day_overlay (for the Day mode)
type HourRow = {
  hour_of_day: number;
  actual_jobs: number;
  actual_vehicles: number;
  actual_drivers: number;
};

type PlannedShiftRow = {
  shift_date: string;
  driver_name: string;
  status: string | null;
  start_hhmm: string | null;
  end_hhmm: string | null;
  shift_type_code: string | null;
  counts_as_worked_hours: boolean | null;
};

type Props = {
  initialDate?: Date | null;
  fleet?: string | null;
};

// NZ weeks start Monday for this app
const NZ_WEEK_STARTS_ON = 1 as const;

const NON_WORKING_CODES_SET = new Set([
  "LEAVE",
  "SICK",
  "UNAVAILABLE",
  "TIME_OFF",
  "TIMEOFF",
  "OFFICE",
]);

const formatHour = (hour: number) => {
  if (hour === 0) return "12a";
  if (hour < 12) return `${hour}a`;
  if (hour === 12) return "12p";
  return `${hour - 12}p`;
};

const safeNum = (v: number | null | undefined) => Number(v || 0);

// Parse "HH:MM" to float hours (e.g. "13:30" -> 13.5)
const hhmmToFloat = (hhmm: string | null | undefined): number | null => {
  if (!hhmm) return null;
  const [hStr, mStr] = hhmm.split(":");
  const h = Number(hStr);
  const m = Number(mStr || 0);
  if (Number.isNaN(h)) return null;
  return h + m / 60;
};

// Given a working shift row, return the number of hours rostered (float, 0 if invalid)
const shiftHours = (s: PlannedShiftRow): number => {
  const start = hhmmToFloat(s.start_hhmm);
  const end = hhmmToFloat(s.end_hhmm);
  if (start === null || end === null) return 0;
  let duration = end - start;
  if (duration <= 0) duration += 24; // cross-midnight
  return duration;
};

// Is the driver rostered during hour [h, h+1)?
const shiftCoversHour = (s: PlannedShiftRow, hour: number): boolean => {
  const start = hhmmToFloat(s.start_hhmm);
  const end = hhmmToFloat(s.end_hhmm);
  if (start === null || end === null) return false;
  let effectiveEnd = end;
  if (end <= start) effectiveEnd = end + 24;

  if (hour + 1 > start && hour < effectiveEnd) return true;
  if (end <= start) {
    if (hour + 25 > start && hour + 24 < effectiveEnd) return true;
  }
  return false;
};

const isWorkingShift = (s: PlannedShiftRow): boolean => {
  if (s.status === "cancelled") return false;
  const code = (s.shift_type_code || "").toString().trim().toUpperCase();
  if (NON_WORKING_CODES_SET.has(code)) return false;
  if (s.counts_as_worked_hours === false) return false;
  return true;
};

const ActualPerformanceTab: React.FC<Props> = ({
  initialDate = null,
  fleet = null,
}) => {
  const [mode, setMode] = useState<RangeMode>("day");
  const [anchor, setAnchor] = useState<Date>(initialDate || new Date());
  const [loading, setLoading] = useState(false);
  const [longJobMode, setLongJobMode] = useState<LongJobMode>("all");

  // Data for day mode (hourly) + the single range row for that day (for distinct vehicles)
  const [hourRows, setHourRows] = useState<HourRow[]>([]);
  const [dayRangeRow, setDayRangeRow] = useState<RangeDayRow | null>(null);

  // Data for week / month mode
  const [rangeRows, setRangeRows] = useState<RangeDayRow[]>([]);

  // Raw planned shift rows (working shifts only, start/end included) for the range
  const [plannedShifts, setPlannedShifts] = useState<PlannedShiftRow[]>([]);

  const { startDate, endDate, label } = useMemo(() => {
    if (mode === "day") {
      return {
        startDate: anchor,
        endDate: anchor,
        label: `${format(anchor, "EEEE")} ${formatDate(anchor)}`,
      };
    }

    if (mode === "week") {
      const ws = startOfWeek(anchor, { weekStartsOn: NZ_WEEK_STARTS_ON });
      const we = endOfWeek(anchor, { weekStartsOn: NZ_WEEK_STARTS_ON });
      return {
        startDate: ws,
        endDate: we,
        label: `${formatDate(ws)} – ${formatDate(we)}`,
      };
    }

    const ms = startOfMonth(anchor);
    const me = endOfMonth(anchor);
    return {
      startDate: ms,
      endDate: me,
      // Deliberately kept as a spelled-out month/year (not formatDate) --
      // "August 2026" isn't a DD/MM-vs-MM/DD ambiguity the tenant date
      // format setting needs to resolve, and forcing it through formatDate
      // would show a meaningless day number for a month-level label.
      label: format(anchor, "MMMM yyyy"),
    };
  }, [anchor, mode]);

  const startStr = format(startDate, "yyyy-MM-dd");
  const endStr = format(endDate, "yyyy-MM-dd");

  const loadData = async () => {
    setLoading(true);
    try {
      // Always pull the range summary (even for day mode — single row — so we get distinct_vehicles)
      const rangeRes = await supabase.rpc(
        "rpc_taxicaller_range_actual_by_day",
        {
          p_start_date: startStr,
          p_end_date: endStr,
          p_fleet: fleet || null,
          p_long_job_mode: longJobMode,
        }
      );
      if (rangeRes.error) throw rangeRes.error;
      const rangeData = (rangeRes.data || []) as RangeDayRow[];

      if (mode === "day") {
        const hourRes = await supabase.rpc("rpc_taxicaller_day_overlay", {
          p_selected_date: startStr,
          p_fleet: fleet || null,
          p_long_job_mode: longJobMode,
        });
        if (hourRes.error) throw hourRes.error;
        setHourRows((hourRes.data || []) as HourRow[]);
        setDayRangeRow(rangeData[0] || null);
        setRangeRows([]);
      } else {
        setRangeRows(rangeData);
        setHourRows([]);
        setDayRangeRow(null);
      }

      // Planned coverage (drivers scheduled) with start/end hhmm for hour-by-hour overlap
      const plannedRes = await supabase
        .from("vw_roster_shifts_enriched")
        .select(
          "shift_date, driver_name, status, start_hhmm, end_hhmm, shift_type_code, counts_as_worked_hours"
        )
        .gte("shift_date", startStr)
        .lte("shift_date", endStr);

      if (plannedRes.error) throw plannedRes.error;
      setPlannedShifts(
        ((plannedRes.data || []) as PlannedShiftRow[]).filter(isWorkingShift)
      );
    } catch (err) {
      console.error("Failed to load actual performance data", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, startStr, endStr, longJobMode, fleet]);

  // Group shifts by date for per-day computations
  const shiftsByDate = useMemo(() => {
    const map = new Map<string, PlannedShiftRow[]>();
    for (const s of plannedShifts) {
      const list = map.get(s.shift_date) || [];
      list.push(s);
      map.set(s.shift_date, list);
    }
    return map;
  }, [plannedShifts]);

  // Planned drivers per hour for the currently-selected day (day mode)
  const plannedByHourToday = useMemo(() => {
    const arr = new Array<number>(24).fill(0);
    const todayShifts = shiftsByDate.get(startStr) || [];
    for (let hour = 0; hour < 24; hour++) {
      const names = new Set<string>();
      for (const s of todayShifts) {
        if (shiftCoversHour(s, hour)) names.add(s.driver_name);
      }
      arr[hour] = names.size;
    }
    return arr;
  }, [shiftsByDate, startStr]);

  // Per-day stats in the range: headcount, peak drivers-on-any-hour, driver-hours rostered
  const perDayPlanned = useMemo(() => {
    const out = new Map<
      string,
      { headcount: number; peakHourCover: number; driverHours: number }
    >();
    for (const [date, shifts] of shiftsByDate.entries()) {
      const driverNames = new Set<string>();
      let driverHours = 0;
      for (const s of shifts) {
        driverNames.add(s.driver_name);
        driverHours += shiftHours(s);
      }

      // compute peak hour cover
      let peakHourCover = 0;
      for (let hour = 0; hour < 24; hour++) {
        const names = new Set<string>();
        for (const s of shifts) {
          if (shiftCoversHour(s, hour)) names.add(s.driver_name);
        }
        if (names.size > peakHourCover) peakHourCover = names.size;
      }

      out.set(date, {
        headcount: driverNames.size,
        peakHourCover,
        driverHours,
      });
    }
    return out;
  }, [shiftsByDate]);

  // Chart data builder
  const chartData = useMemo(() => {
    if (mode === "day") {
      const map = new Map<number, any>();
      for (let h = 0; h < 24; h++) {
        map.set(h, {
          key: formatHour(h),
          actual_jobs: 0,
          actual_vehicles: 0,
          planned_drivers: plannedByHourToday[h] || 0,
        });
      }
      for (const r of hourRows) {
        const entry = map.get(r.hour_of_day);
        if (!entry) continue;
        entry.actual_jobs = safeNum(r.actual_jobs);
        entry.actual_vehicles = safeNum(r.actual_vehicles);
      }
      return Array.from(map.values());
    }

    return rangeRows.map((r) => {
      const planned = perDayPlanned.get(r.active_date);
      return {
        key:
          mode === "week"
            ? `${r.dow_label} ${format(new Date(r.active_date + "T00:00:00"), "d")}`
            : format(new Date(r.active_date + "T00:00:00"), "d"),
        active_date: r.active_date,
        distinct_jobs: safeNum(r.distinct_jobs),
        distinct_vehicles: safeNum(r.distinct_vehicles),
        total_job_hours: safeNum(r.total_job_hours),
        peak_vehicles: safeNum(r.peak_vehicles),
        peak_drivers: safeNum(r.peak_drivers),
        // For week/month chart, show the peak concurrent drivers rostered
        // (i.e. max of hourly coverage that day) — shows how many hands
        // were actually on-deck at the busiest roster moment.
        planned_drivers: planned?.peakHourCover ?? 0,
        planned_driver_hours: planned?.driverHours ?? 0,
      };
    });
  }, [mode, hourRows, rangeRows, plannedByHourToday, perDayPlanned]);

  // Summary stats
  const summary = useMemo(() => {
    if (mode === "day") {
      const totalJobs = dayRangeRow ? safeNum(dayRangeRow.distinct_jobs) : 0;
      const jobHours = hourRows.reduce((s, r) => s + safeNum(r.actual_jobs), 0);
      const peakVehicles = hourRows.reduce(
        (m, r) => Math.max(m, safeNum(r.actual_vehicles)),
        0
      );
      const uniqueVehicles = dayRangeRow
        ? safeNum(dayRangeRow.distinct_vehicles)
        : 0;
      const peakRow = hourRows.reduce(
        (best, r) =>
          safeNum(r.actual_jobs) > safeNum(best?.actual_jobs || 0) ? r : best,
        hourRows[0] as HourRow | undefined
      );
      const plannedDayInfo = perDayPlanned.get(startStr);
      const plannedPeakCover = plannedDayInfo?.peakHourCover || 0;
      const plannedHeadcount = plannedDayInfo?.headcount || 0;
      const plannedHours = plannedDayInfo?.driverHours || 0;

      const activeHours = hourRows.filter((r) => safeNum(r.actual_jobs) > 0);
      const avgJobsPerHourActive =
        activeHours.length > 0 ? jobHours / activeHours.length : 0;

      return {
        totalJobs,
        jobHours,
        peakVehicles,
        uniqueVehicles,
        peakLabel: peakRow ? formatHour(peakRow.hour_of_day) : "-",
        peakValue: peakRow ? safeNum(peakRow.actual_jobs) : 0,
        plannedPeakCover,
        plannedHeadcount,
        plannedHours,
        dayCount: 1,
        averageJobs: totalJobs,
        averageLabel: "Jobs today",
        avgJobsPerHourActive,
      };
    }

    const dayCount = rangeRows.length || 1;
    const totalJobs = rangeRows.reduce(
      (s, r) => s + safeNum(r.distinct_jobs),
      0
    );
    const jobHours = rangeRows.reduce(
      (s, r) => s + safeNum(r.total_job_hours),
      0
    );
    const peakVehicles = rangeRows.reduce(
      (m, r) => Math.max(m, safeNum(r.peak_vehicles)),
      0
    );
    const uniqueVehiclesMax = rangeRows.reduce(
      (m, r) => Math.max(m, safeNum(r.distinct_vehicles)),
      0
    );
    const uniqueVehiclesAvg =
      rangeRows.length > 0
        ? rangeRows.reduce((s, r) => s + safeNum(r.distinct_vehicles), 0) /
          rangeRows.length
        : 0;
    const peakRow = rangeRows.reduce(
      (best, r) =>
        safeNum(r.distinct_jobs) > safeNum(best?.distinct_jobs || 0)
          ? r
          : best,
      rangeRows[0] as RangeDayRow | undefined
    );

    // Aggregate planned metrics across the range
    let plannedPeakCoverMax = 0;
    let plannedPeakCoverAvg = 0;
    let plannedHoursTotal = 0;
    let plannedHeadcountTotal = 0;
    for (const r of rangeRows) {
      const p = perDayPlanned.get(r.active_date);
      if (!p) continue;
      plannedPeakCoverMax = Math.max(plannedPeakCoverMax, p.peakHourCover);
      plannedPeakCoverAvg += p.peakHourCover;
      plannedHoursTotal += p.driverHours;
      plannedHeadcountTotal += p.headcount;
    }
    plannedPeakCoverAvg =
      rangeRows.length > 0 ? plannedPeakCoverAvg / rangeRows.length : 0;

    const averageJobs = totalJobs / dayCount;

    return {
      totalJobs,
      jobHours,
      peakVehicles,
      uniqueVehicles: uniqueVehiclesMax,
      uniqueVehiclesAvg,
      peakLabel: peakRow
        ? format(new Date(peakRow.active_date + "T00:00:00"), "EEE d MMM")
        : "-",
      peakValue: peakRow ? safeNum(peakRow.distinct_jobs) : 0,
      plannedPeakCoverMax,
      plannedPeakCoverAvg,
      plannedHeadcount: plannedHeadcountTotal,
      plannedHours: plannedHoursTotal,
      dayCount,
      averageJobs,
      averageLabel:
        mode === "week" ? "Avg jobs / day (week)" : "Avg jobs / day (month)",
      avgJobsPerHourActive: 0,
    };
  }, [mode, hourRows, rangeRows, perDayPlanned, dayRangeRow, startStr]);

  const longJobOptions: { value: LongJobMode; label: string }[] = [
    { value: "all", label: "All Jobs" },
    { value: "exclude", label: "Exclude Long" },
    { value: "only", label: "Long Only" },
  ];

  return (
    <div className="space-y-4">
      {/* Top controls — Roster-style date picker + Long Job filter + Refresh */}
      <div className="flex flex-wrap items-center gap-3">
        <RangeDatePicker
          mode={mode}
          onModeChange={(m) => setMode(m as RangeMode)}
          anchor={anchor}
          onAnchorChange={setAnchor}
        />

        <div className="flex items-center gap-1 ml-2">
          <span className="text-xs text-gray-400 mr-1">Long jobs:</span>
          {longJobOptions.map((opt) => (
            <Button
              key={opt.value}
              variant={longJobMode === opt.value ? "default" : "outline"}
              size="sm"
              onClick={() => setLongJobMode(opt.value)}
              className={
                longJobMode === opt.value
                  ? "bg-blue-600 hover:bg-blue-700"
                  : "bg-gray-800 border-gray-600 text-gray-200 hover:bg-gray-700"
              }
            >
              {opt.label}
            </Button>
          ))}
        </div>

        <div className="ml-auto flex items-center gap-2">
          <Badge className="bg-gray-700 text-gray-100 text-xs">
            <CalendarDays className="h-3 w-3 mr-1" />
            {label}
          </Badge>
          <Button
            variant="outline"
            size="sm"
            onClick={loadData}
            disabled={loading}
            className="bg-gray-800 border-gray-600 text-gray-200 hover:bg-gray-700"
          >
            <RefreshCw
              className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`}
            />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-3">
          <div className="text-xs text-gray-400 uppercase">Total Jobs</div>
          <div className="text-2xl font-bold">{summary.totalJobs}</div>
          <div className="text-xs text-gray-500 mt-1">Distinct job IDs</div>
        </div>

        <div className="bg-gray-800 border border-gray-700 rounded-lg p-3">
          <div className="text-xs text-gray-400 uppercase">
            {mode === "day" ? "Jobs Today" : summary.averageLabel}
          </div>
          <div className="text-2xl font-bold">
            {summary.averageJobs.toFixed(mode === "day" ? 0 : 1)}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            {mode === "day"
              ? `${summary.avgJobsPerHourActive.toFixed(1)} avg / active hr`
              : `Over ${summary.dayCount} day${summary.dayCount === 1 ? "" : "s"}`}
          </div>
        </div>

        <div className="bg-emerald-950/40 border border-emerald-700/50 rounded-lg p-3">
          <div className="text-xs text-emerald-300 uppercase">
            Vehicles on Shift
          </div>
          <div className="text-2xl font-bold text-emerald-200">
            {summary.uniqueVehicles}
          </div>
          <div className="text-xs text-emerald-400/80 mt-1">
            {mode === "day"
              ? "Unique vehicle numbers"
              : `Max/day · avg ${(summary as any).uniqueVehiclesAvg?.toFixed(1) ?? "-"}`}
          </div>
        </div>

        <div className="bg-gray-800 border border-gray-700 rounded-lg p-3">
          <div className="text-xs text-gray-400 uppercase">Job-Hours</div>
          <div className="text-2xl font-bold">{summary.jobHours.toFixed(0)}</div>
          <div className="text-xs text-gray-500 mt-1">
            Sum of hourly activity
          </div>
        </div>

        <div className="bg-gray-800 border border-gray-700 rounded-lg p-3">
          <div className="text-xs text-gray-400 uppercase">
            {mode === "day" ? "Peak Hour" : "Busiest Day"}
          </div>
          <div className="text-xl font-bold">{summary.peakLabel}</div>
          <div className="text-xs text-gray-400">{summary.peakValue} jobs</div>
        </div>

        <div className="bg-amber-950/40 border border-amber-700/50 rounded-lg p-3">
          <div className="text-xs text-amber-300 uppercase">
            Planned Cover
          </div>
          {mode === "day" ? (
            <>
              <div className="text-2xl font-bold text-amber-200">
                {(summary as any).plannedPeakCover || 0}
              </div>
              <div className="text-xs text-amber-400/80 mt-1">
                Peak concurrent · {(summary as any).plannedHeadcount || 0}{" "}
                rostered · {((summary as any).plannedHours || 0).toFixed(0)} hrs
              </div>
            </>
          ) : (
            <>
              <div className="text-2xl font-bold text-amber-200">
                {((summary as any).plannedHours || 0).toFixed(0)}
                <span className="text-sm font-normal text-amber-300/80 ml-1">
                  hrs
                </span>
              </div>
              <div className="text-xs text-amber-400/80 mt-1">
                Peak {(summary as any).plannedPeakCoverMax || 0} · avg{" "}
                {((summary as any).plannedPeakCoverAvg || 0).toFixed(1)} /day
              </div>
            </>
          )}
        </div>
      </div>

      {/* Chart */}
      <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
        <div className="text-sm font-semibold mb-3 text-gray-200 flex items-center gap-2">
          {mode === "day" ? (
            <>
              <BarChart3 className="h-4 w-4 text-blue-400" />
              Actual Jobs per Hour — with Vehicles &amp; Planned Cover
            </>
          ) : (
            <>
              <TrendingUp className="h-4 w-4 text-blue-400" />
              Actual Jobs per Day — with Unique Vehicles &amp; Peak Planned
              Cover
            </>
          )}
        </div>

        <div style={{ width: "100%", height: 380 }}>
          <ResponsiveContainer>
            <ComposedChart data={chartData}>
              <CartesianGrid stroke="#374151" strokeDasharray="3 3" />
              <XAxis dataKey="key" stroke="#9CA3AF" fontSize={12} />
              <YAxis stroke="#9CA3AF" fontSize={12} />
              <Tooltip
                contentStyle={{
                  background: "#111827",
                  border: "1px solid #374151",
                  borderRadius: 6,
                  color: "#F9FAFB",
                }}
              />
              <Legend />
              <Bar
                dataKey={mode === "day" ? "actual_jobs" : "distinct_jobs"}
                name={mode === "day" ? "Actual Jobs / Hour" : "Distinct Jobs"}
                fill="#3B82F6"
                radius={[4, 4, 0, 0]}
              />
              {mode === "day" ? (
                <Line
                  type="monotone"
                  dataKey="actual_vehicles"
                  name="Active Vehicles / Hour"
                  stroke="#10B981"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                />
              ) : (
                <Line
                  type="monotone"
                  dataKey="distinct_vehicles"
                  name="Unique Vehicles on Shift"
                  stroke="#10B981"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                />
              )}
              <Line
                type="monotone"
                dataKey="planned_drivers"
                name={
                  mode === "day"
                    ? "Planned Drivers / Hour"
                    : "Peak Planned Drivers"
                }
                stroke="#F59E0B"
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={{ r: 3 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        <div className="text-[11px] text-gray-500 mt-2">
          {mode === "day" ? (
            <>
              Amber line = drivers whose rostered start/end times cover that
              hour (not a flat daily count). Green = unique vehicles that ran
              jobs that hour. Cancelled jobs and &quot;Admin Tracking&quot; are
              excluded.
            </>
          ) : (
            <>
              Amber line = peak concurrent drivers on shift during that day
              (max across all hours, based on rostered start/end times). Green =
              unique vehicles that ran at least one job.
            </>
          )}
        </div>
      </div>

      {/* Range breakdown table (week / month) */}
      {mode !== "day" && rangeRows.length > 0 && (
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
          <div className="text-sm font-semibold mb-3 text-gray-200">
            Daily Breakdown
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-400 border-b border-gray-700">
                  <th className="py-2 pr-4">Date</th>
                  <th className="py-2 pr-4 text-right">Actual Jobs</th>
                  <th className="py-2 pr-4 text-right">Job-Hours</th>
                  <th className="py-2 pr-4 text-right">Unique Vehicles</th>
                  <th className="py-2 pr-4 text-right">Peak Vehicles</th>
                  <th className="py-2 pr-4 text-right">Peak Hour</th>
                  <th className="py-2 pr-4 text-right">Peak Cover</th>
                  <th className="py-2 pr-4 text-right">Driver-Hrs</th>
                  <th className="py-2 pr-4 text-right">Utilisation</th>
                </tr>
              </thead>
              <tbody>
                {rangeRows.map((r) => {
                  const planned = perDayPlanned.get(r.active_date);
                  const peakCover = planned?.peakHourCover || 0;
                  const plannedHrs = planned?.driverHours || 0;
                  const uniqueV = safeNum(r.distinct_vehicles);
                  const peakV = safeNum(r.peak_vehicles);
                  // Utilisation against peak concurrent planned cover
                  const utilPct =
                    peakCover > 0 ? (peakV / peakCover) * 100 : 0;
                  const utilColor =
                    utilPct >= 100
                      ? "text-red-400"
                      : utilPct >= 80
                      ? "text-amber-400"
                      : utilPct > 0
                      ? "text-emerald-400"
                      : "text-gray-500";

                  return (
                    <tr
                      key={r.active_date}
                      className="border-b border-gray-700/50 hover:bg-gray-700/30"
                    >
                      <td className="py-2 pr-4 font-medium">
                        {format(
                          new Date(r.active_date + "T00:00:00"),
                          "EEE d MMM"
                        )}
                      </td>
                      <td className="py-2 pr-4 text-right font-semibold text-blue-300">
                        {r.distinct_jobs}
                      </td>
                      <td className="py-2 pr-4 text-right text-gray-300">
                        {safeNum(r.total_job_hours).toFixed(0)}
                      </td>
                      <td className="py-2 pr-4 text-right text-emerald-200 font-semibold">
                        {uniqueV}
                      </td>
                      <td className="py-2 pr-4 text-right text-emerald-300">
                        {peakV}
                      </td>
                      <td className="py-2 pr-4 text-right text-gray-300">
                        {formatHour(r.peak_hour)} ({r.peak_hour_jobs})
                      </td>
                      <td className="py-2 pr-4 text-right text-amber-300 font-semibold">
                        {peakCover}
                      </td>
                      <td className="py-2 pr-4 text-right text-amber-200">
                        {plannedHrs.toFixed(0)}
                      </td>
                      <td
                        className={`py-2 pr-4 text-right font-medium ${utilColor}`}
                      >
                        {peakCover > 0 ? `${utilPct.toFixed(0)}%` : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t border-gray-700">
                  <td className="py-2 pr-4 font-semibold text-gray-300">
                    Totals
                  </td>
                  <td className="py-2 pr-4 text-right font-bold text-blue-200">
                    {summary.totalJobs}
                  </td>
                  <td className="py-2 pr-4 text-right text-gray-300">
                    {summary.jobHours.toFixed(0)}
                  </td>
                  <td className="py-2 pr-4 text-right text-emerald-200 font-bold">
                    {summary.uniqueVehicles} max
                  </td>
                  <td className="py-2 pr-4 text-right text-emerald-300">
                    {summary.peakVehicles}
                  </td>
                  <td className="py-2 pr-4 text-right text-gray-500">—</td>
                  <td className="py-2 pr-4 text-right text-amber-300 font-bold">
                    {(summary as any).plannedPeakCoverMax || 0} max
                  </td>
                  <td className="py-2 pr-4 text-right text-amber-200 font-bold">
                    {((summary as any).plannedHours || 0).toFixed(0)}
                  </td>
                  <td className="py-2 pr-4 text-right text-gray-500">—</td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div className="text-[11px] text-gray-500 mt-2">
            <b>Peak Cover</b> = most drivers concurrently rostered on any hour
            of that day (based on rostered start/end times).{" "}
            <b>Driver-Hrs</b> = total rostered hours for the day. Utilisation
            compares the day's Peak Vehicles against Peak Cover.
          </div>
        </div>
      )}
    </div>
  );
};

export default ActualPerformanceTab;
